import React, { useState, useEffect } from "react";

/**
 * Step 4: Conflict Resolution
 * Display and resolve actual conflicts detected during file upload
 * Uses real conflict data from backend API (stored in migrationData.conflicts)
 */
function Step4ConflictResolution({
  migrationData,
  updateMigrationData,
  nextStep,
  prevStep,
}) {
  const [conflicts, setConflicts] = useState([]);
  const [resolutions, setResolutions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConflicts();
  }, []);

  const loadConflicts = () => {
    setLoading(true);

    // Load conflicts from migrationData (stored from Step 2 API responses)
    const storedConflicts = migrationData.conflicts || [];

    // Transform backend conflict format to display format if needed
    const formattedConflicts = storedConflicts.map((conflict, idx) => ({
      id: conflict.id || `conflict_${idx}`,
      type: conflict.type || "unknown",
      dataType: conflict.dataType || "unknown",
      severity: conflict.severity || "medium",
      message: formatConflictMessage(conflict),
      existingData: conflict.existing || {},
      newData: conflict.imported || {},
      // Map backend suggestions to options
      options: (conflict.suggestions || []).map((s) => s.action),
      suggestions: conflict.suggestions || [],
      confidence: conflict.confidence,
      canProceed: conflict.canProceed !== false,
    }));

    setConflicts(formattedConflicts);

    // Auto-select default resolution (skip_import for safety)
    const autoResolutions = {};
    formattedConflicts.forEach((c) => {
      // Default to skip_import if available, otherwise first option
      if (c.options.includes("skip_import")) {
        autoResolutions[c.id] = "skip_import";
      } else if (c.options.length > 0) {
        autoResolutions[c.id] = c.options[0];
      }
    });

    // Load any previously saved resolutions
    if (migrationData.resolvedConflicts) {
      Object.assign(autoResolutions, migrationData.resolvedConflicts);
    }

    setResolutions(autoResolutions);
    setLoading(false);
  };

  // Generate human-readable conflict message
  const formatConflictMessage = (conflict) => {
    if (conflict.message) return conflict.message;

    switch (conflict.type) {
      case "duplicate_service":
        return `Service "${conflict.imported?.name || "Unknown"}" may already exist in the system`;
      case "similar_service":
        return `Service "${conflict.imported?.name}" is similar to existing "${conflict.existing?.name}" (${Math.round((conflict.confidence || 0) * 100)}% match)`;
      case "duplicate_customer":
        return `Customer "${conflict.imported?.email || conflict.imported?.firstName || "Unknown"}" already exists`;
      case "exact_email":
        return `Customer email "${conflict.imported?.email}" already exists`;
      case "exact_phone":
        return `Customer phone "${conflict.imported?.phone}" already exists`;
      case "duplicate_staff":
        return `Staff member "${conflict.imported?.name || conflict.imported?.firstName || "Unknown"}" may already exist`;
      case "overlapping_appointment":
        return `Appointment overlaps with an existing booking`;
      case "missing_reference":
        return `Appointment references a service or customer that doesn't exist`;
      default:
        return `Potential conflict detected for ${conflict.type?.replace(/_/g, " ")}`;
    }
  };

  // Get readable label for resolution option
  const getOptionLabel = (action) => {
    const labels = {
      skip_import: "Skip (don't import this record)",
      update_existing: "Replace existing with import data",
      keep_both: "Keep both (import as new)",
      merge: "Merge data (combine fields)",
      merge_data: "Merge import data into existing",
      manual_map: "Select existing record to link",
      skip_staff: "Import without staff assignment",
      import_anyway: "Import anyway (fix reference later)",
      create_placeholder: "Create placeholder & import",
    };
    return (
      labels[action] ||
      action.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  // Get description for resolution option
  const getOptionDescription = (conflict, action) => {
    const suggestion = conflict.suggestions?.find((s) => s.action === action);
    return suggestion?.description || "";
  };

  const handleResolutionChange = (conflictId, resolution) => {
    setResolutions({
      ...resolutions,
      [conflictId]: resolution,
    });
  };

  const handleContinue = () => {
    // Store the conflicts and resolutions for import execution
    updateMigrationData({
      conflicts,
      resolvedConflicts: resolutions,
    });
    nextStep();
  };

  // Group conflicts by data type for better organization
  const groupedConflicts = conflicts.reduce((acc, conflict) => {
    const type = conflict.dataType || "other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(conflict);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading conflicts...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Step 4: Conflict Resolution</h2>

      {conflicts.length === 0 ? (
        <div style={styles.noConflicts}>
          <div style={styles.successIcon}>✓</div>
          <h3>No Conflicts Found</h3>
          <p>Your data looks good! No duplicates or conflicts detected.</p>
        </div>
      ) : (
        <>
          <p style={styles.subtitle}>
            We found {conflicts.length} potential conflict
            {conflicts.length > 1 ? "s" : ""}. Please review and choose how to
            handle each one.
          </p>

          {/* Summary by data type */}
          {migrationData.conflictData && (
            <div style={styles.summaryBar}>
              {Object.entries(migrationData.conflictData.byDataType || {}).map(
                ([type, count]) =>
                  count > 0 && (
                    <span key={type} style={styles.summaryItem}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}: {count}
                    </span>
                  ),
              )}
            </div>
          )}

          {/* Display conflicts grouped by data type */}
          {Object.entries(groupedConflicts).map(([dataType, typeConflicts]) => (
            <div key={dataType} style={styles.conflictGroup}>
              <h3 style={styles.groupTitle}>
                {dataType.charAt(0).toUpperCase() + dataType.slice(1)} Conflicts
                ({typeConflicts.length})
              </h3>

              {typeConflicts.map((conflict) => (
                <div key={conflict.id} style={styles.conflictCard}>
                  <div style={styles.conflictHeader}>
                    <span
                      style={{
                        ...styles.severityBadge,
                        ...(conflict.severity === "high"
                          ? styles.severityHigh
                          : conflict.severity === "low"
                            ? styles.severityLow
                            : {}),
                      }}
                    >
                      {conflict.severity === "high"
                        ? "❌"
                        : conflict.severity === "low"
                          ? "ℹ️"
                          : "⚠️"}{" "}
                      {conflict.severity}
                    </span>
                    <span style={styles.conflictType}>
                      {conflict.type.replace(/_/g, " ")}
                      {conflict.confidence &&
                        ` (${Math.round(conflict.confidence * 100)}% confidence)`}
                    </span>
                  </div>

                  <p style={styles.conflictMessage}>{conflict.message}</p>

                  <div style={styles.dataComparison}>
                    <div style={styles.dataColumn}>
                      <h4>Existing Data</h4>
                      <pre style={styles.dataPreview}>
                        {JSON.stringify(conflict.existingData, null, 2)}
                      </pre>
                    </div>
                    <div style={styles.dataColumn}>
                      <h4>New (Import) Data</h4>
                      <pre style={styles.dataPreview}>
                        {JSON.stringify(conflict.newData, null, 2)}
                      </pre>
                    </div>
                  </div>

                  {conflict.options.length > 0 && (
                    <div style={styles.resolutionOptions}>
                      <label style={styles.resolutionLabel}>
                        How should we handle this?
                      </label>
                      <select
                        value={
                          resolutions[conflict.id] ||
                          conflict.options[0] ||
                          "skip_import"
                        }
                        onChange={(e) =>
                          handleResolutionChange(conflict.id, e.target.value)
                        }
                        style={styles.select}
                      >
                        {conflict.options.map((option) => (
                          <option key={option} value={option}>
                            {getOptionLabel(option)}
                          </option>
                        ))}
                      </select>
                      {getOptionDescription(
                        conflict,
                        resolutions[conflict.id],
                      ) && (
                        <p style={styles.optionHint}>
                          {getOptionDescription(
                            conflict,
                            resolutions[conflict.id],
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Bulk actions */}
          <div style={styles.bulkActions}>
            <span style={styles.bulkLabel}>Quick actions:</span>
            <button
              onClick={() => {
                const newResolutions = {};
                conflicts.forEach((c) => {
                  newResolutions[c.id] = c.options.includes("skip_import")
                    ? "skip_import"
                    : c.options[0];
                });
                setResolutions(newResolutions);
              }}
              style={styles.bulkButton}
            >
              Skip All
            </button>
            <button
              onClick={() => {
                const newResolutions = {};
                conflicts.forEach((c) => {
                  newResolutions[c.id] = c.options.includes("update_existing")
                    ? "update_existing"
                    : c.options[0];
                });
                setResolutions(newResolutions);
              }}
              style={styles.bulkButton}
            >
              Update All Existing
            </button>
            <button
              onClick={() => {
                const newResolutions = {};
                conflicts.forEach((c) => {
                  newResolutions[c.id] = c.options.includes("keep_both")
                    ? "keep_both"
                    : c.options[0];
                });
                setResolutions(newResolutions);
              }}
              style={styles.bulkButton}
            >
              Keep All (Duplicates OK)
            </button>
          </div>
        </>
      )}

      <div style={styles.actions}>
        <button onClick={prevStep} style={styles.backButton}>
          ← Back
        </button>
        <button onClick={handleContinue} style={styles.continueButton}>
          Continue →
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "8px",
    color: "#111",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "24px",
  },
  loading: {
    textAlign: "center",
    padding: "48px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #2196F3",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 16px",
  },
  noConflicts: {
    textAlign: "center",
    padding: "48px 24px",
  },
  successIcon: {
    fontSize: "64px",
    color: "#4CAF50",
    marginBottom: "16px",
  },
  summaryBar: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
    padding: "12px 16px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    flexWrap: "wrap",
  },
  summaryItem: {
    fontSize: "13px",
    color: "#666",
    padding: "4px 12px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    border: "1px solid #e0e0e0",
  },
  conflictGroup: {
    marginBottom: "32px",
  },
  groupTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: "2px solid #e0e0e0",
  },
  conflictCard: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "16px",
    backgroundColor: "#fafafa",
  },
  conflictHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  severityBadge: {
    padding: "4px 12px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    backgroundColor: "#fff3cd",
    color: "#856404",
    textTransform: "uppercase",
  },
  severityHigh: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
  },
  severityLow: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
  },
  conflictType: {
    fontSize: "12px",
    color: "#666",
    textTransform: "capitalize",
  },
  conflictMessage: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "16px",
  },
  dataComparison: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "16px",
  },
  dataColumn: {
    backgroundColor: "#fff",
    padding: "12px",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
  },
  dataPreview: {
    fontSize: "12px",
    color: "#333",
    backgroundColor: "#f5f5f5",
    padding: "8px",
    borderRadius: "4px",
    overflow: "auto",
    maxHeight: "120px",
    margin: "8px 0 0 0",
  },
  resolutionOptions: {
    marginTop: "16px",
  },
  resolutionLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#333",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  optionHint: {
    fontSize: "12px",
    color: "#666",
    marginTop: "6px",
    fontStyle: "italic",
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "24px",
    marginBottom: "8px",
    padding: "16px",
    backgroundColor: "#f0f7ff",
    borderRadius: "8px",
    flexWrap: "wrap",
  },
  bulkLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  },
  bulkButton: {
    padding: "8px 16px",
    backgroundColor: "#fff",
    color: "#2196F3",
    border: "1px solid #2196F3",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  backButton: {
    padding: "12px 24px",
    backgroundColor: "#fff",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  continueButton: {
    padding: "12px 32px",
    backgroundColor: "#2196F3",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default Step4ConflictResolution;
